/**
 * Export Validation Utilities
 * Provides functions to validate data availability before export
 * and handles backend API responses for export validation
 */

import { translate } from "../translate/translate";
import { ExportValidationResult, DataEmptyReason, ExportApiResponse, BackendErrorCode } from "../types/export-validation.types";
import { ExportType } from "../types/export.types";

/**
 * Validates if there is exportable data
 * @param data - Array of data to validate
 * @param exportType - Type of export being performed
 * @returns ExportValidationResult with validation status
 */
export function validateExportableData(
  data: any[] | undefined | null,
  exportType: ExportType
): ExportValidationResult {
  // Check if data exists and is an array
  if (!Array.isArray(data)) {
    return {
      isValid: false,
      message: getValidationErrorMessage(exportType, DataEmptyReason.INVALID_STATE),
      reason: DataEmptyReason.INVALID_STATE,
      rowCount: 0,
    };
  }

  // Check if data array is empty
  if (data.length === 0) {
    return {
      isValid: false,
      message: getValidationErrorMessage(exportType, DataEmptyReason.NO_ROWS),
      reason: DataEmptyReason.NO_ROWS,
      rowCount: 0,
    };
  }

  return {
    isValid: true,
    rowCount: data.length,
  };
}

/**
 * Validates the backend API response for export
 * Checks if the backend returned a NO_DATA_AVAILABLE error or similar
 * @param response - API response from export endpoint
 * @returns ExportValidationResult indicating if export can proceed
 */
export function validateExportApiResponse(
  response: ExportApiResponse | undefined | null
): ExportValidationResult {
  if (!response) {
    return {
      isValid: false,
      message: getDefaultValidationMessage(),
      reason: DataEmptyReason.INVALID_STATE,
    };
  }

  // Handle array response from backend (e.g., frontend validations/Django Rest Framework wrappers)
  const responseData = Array.isArray(response) ? response[0] : response;

  // Check for NO_DATA_AVAILABLE error from backend
  if (responseData?.code === BackendErrorCode.NO_DATA_AVAILABLE || 
      responseData?.code === "NO_DATA_AVAILABLE") {
    return {
      isValid: false,
      message: responseData.error || getDefaultValidationMessage(),
      reason: DataEmptyReason.NO_ROWS,
    };
  }

  // Check if response has required fields for successful export
  if ('task_id' in responseData && responseData.task_id || ('status' in responseData && (responseData.status === "FINISHED" || responseData.status === "COMPLETED") && 'file_url' in responseData && responseData.file_url)) {
    return {
      isValid: true,
    };
  }

  // If no task_id and no file_url, it's an error
  return {
    isValid: false,
    message: ('error' in responseData ? responseData.error : undefined) || getDefaultValidationMessage(),
    reason: DataEmptyReason.UNKNOWN,
  };
}

/**
 * Gets the appropriate error message based on export type and reason
 * @param exportType - Type of export
 * @param reason - Reason for validation failure
 * @returns Localized error message
 */
function getValidationErrorMessage(
  exportType: ExportType,
  reason: DataEmptyReason
): string {
  const defaultMessage = getDefaultValidationMessage();

  // Map specific messages by export type
  const messagesByType: Record<ExportType, Record<DataEmptyReason, string>> = {
    [ExportType.EXPORT_ANALYSIS]: {
      [DataEmptyReason.NO_ROWS]: `${defaultMessage}`,
      [DataEmptyReason.NO_FILTERS_APPLIED]: `${defaultMessage}`,
      [DataEmptyReason.INVALID_STATE]: `${defaultMessage}`,
      [DataEmptyReason.UNKNOWN]: `${defaultMessage}`,
    },
    [ExportType.EXPORT_MATERIALS_DEMANDS]: {
      [DataEmptyReason.NO_ROWS]: `${defaultMessage}`,
      [DataEmptyReason.NO_FILTERS_APPLIED]: `${defaultMessage}`,
      [DataEmptyReason.INVALID_STATE]: `${defaultMessage}`,
      [DataEmptyReason.UNKNOWN]: `${defaultMessage}`,
    },
    [ExportType.EXPORT_PO_COMPONENTS]: {
      [DataEmptyReason.NO_ROWS]: `${defaultMessage}`,
      [DataEmptyReason.NO_FILTERS_APPLIED]: `${defaultMessage}`,
      [DataEmptyReason.INVALID_STATE]: `${defaultMessage}`,
      [DataEmptyReason.UNKNOWN]: `${defaultMessage}`,
    },
    [ExportType.EXPORT_STOCK]: {
      [DataEmptyReason.NO_ROWS]: `${defaultMessage}`,
      [DataEmptyReason.NO_FILTERS_APPLIED]: `${defaultMessage}`,
      [DataEmptyReason.INVALID_STATE]: `${defaultMessage}`,
      [DataEmptyReason.UNKNOWN]: `${defaultMessage}`,
    },
  };

  return messagesByType[exportType]?.[reason] || defaultMessage;
}

/**
 * Gets the default validation error message
 * Using the custom message from the card requirements
 * @returns Localized error message
 */
export function getDefaultValidationMessage(): string {
  return translate.t("export_no_data_available") ||
    "Não foi possível exportar a planilha. Não há dados disponíveis para exportação. " +
    "Por favor, verifique os filtros ou realize uma nova análise para gerar conteúdo.";
}

/**
 * Validates if data exists and is not empty
 * Simple check for array existence and non-empty length
 * @param data - Data to validate
 * @returns boolean
 */
export function hasExportableData(data: any[] | undefined | null): boolean {
  return Array.isArray(data) && data.length > 0;
}

/**
 * Gets row count from data
 * @param data - Data array
 * @returns Number of rows
 */
export function getExportDataRowCount(data: any[] | undefined | null): number {
  return Array.isArray(data) ? data.length : 0;
}

